import { useState, useMemo } from 'react';
import MeasuresTable from '../../features/MeasuresTable';
import { MeasureGraphs } from './MeasureScores';
import {
  Measure,
  MeasureTableRow,
  PopulationAndType,
} from '../../utils/dataTypes';
import { convertMeasureTableRowToMeasure } from '../../utils/helpers';
import { components } from '../../api/types';

type Quantiles = components['schemas']['Quantiles'] | null;
type Props = {
  /* Diagnosis data */
  diagnosisName: string;
  diagnosisLabel: string | null;
  oddsRatio: number | null;

  /* Measures data */
  measureNames: string[];
  actualScores: (number | null)[];
  actualPercentileRanks: (number | null)[];
  correlationPercents: number[];
  takenMeasureMask: boolean[];
  quantiles: Quantiles[];

  /* Population data */
  population: PopulationAndType;
};

/**
 * JSX Component containing client diagnosis details
 * 
 * @param diagnosis: string - client diagnosis name
 * 
 * @returns Diagnosis details element
 */
export const DiagnosisSection = ({
  /* Diagnosis data */
  diagnosisName,
  diagnosisLabel,
  oddsRatio,

  /* Measures data */
  measureNames,
  actualScores,
  actualPercentileRanks,
  correlationPercents,
  takenMeasureMask,
  quantiles,

  /* Population data */
  population,
}: Props): JSX.Element => {
  const [descriptionIsVisible, setDescriptionIsVisible] = useState(false);
  const [selectedMeasures, setSelectedMeasures] = useState<Measure[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);

  const takenMeasures = useMemo(() => (
    measureNames
    .map((_, i: number) => {
      return {
        measureName: measureNames[i],
        actualScore: actualScores[i],
        percentile: Number(((actualPercentileRanks[i] ?? 0) * 100).toFixed(2)),
        correlationPercent: correlationPercents[i],
        quantiles: quantiles[i],
      };
    })
    .filter((_, i: number) => takenMeasureMask[i])
  ), [measureNames, actualScores, actualPercentileRanks, correlationPercents, takenMeasureMask, quantiles]);

  const measureTableProps = useMemo(() => ({
    measureNames: takenMeasures.map((m) => m.measureName),
    actualScores: takenMeasures.map((m) => m.actualScore as number),
    percentileRanks: takenMeasures.map((m) => m.percentile as number),
    correlationPercents: takenMeasures.map((m) => m.correlationPercent as number),
  }), [takenMeasures]);

  /* When user selects rows in table,
   * selected measures are plotted in box-and-whisker */
  const onSelectionChanged = (params: any) => {
    if (gridApi) {
      const selectedDataRows = gridApi.getSelectedRows();
      const selectedDataMeasures = selectedDataRows.map((measureRow: MeasureTableRow) => {
        const measure = convertMeasureTableRowToMeasure(measureRow);
        return takenMeasures.find((dm) => dm.measureName === measure.measureName) || null;
      });
      setSelectedMeasures(selectedDataMeasures);
    }
  };

  

  /* Get all measureNames, correlationPercent, actualScore, percentile, and predictedScore */
  // useEffect(() => {
  //   const loadMeasuresForDiagnosis = () => {
  //     /* Derive the names list from returned measures */
  //     const diagnosisStats = diagnosesStats[diagnosis];
  //     const specificMeasures = diagnosisStats.correlatedMeasures.filter((measure: Measure) => measure.actualScore != null);
  //     setDiagnosisMeasures(specificMeasures);
  //   }
  //   if (Object.values(diagnosesStats).length) {
  //     loadMeasuresForDiagnosis();
  //   }
  // }, [diagnosis, label, oddsRatio, population, diagnosesStats]);






  const toggleDiagnosisSection = () => {
    setDescriptionIsVisible(!descriptionIsVisible);
  };



  oddsRatio = oddsRatio || 0;


  /* Get measures below and above average */
  const measuresBelowAverage = takenMeasures
    .filter((m) => m.percentile != null && m.percentile < 45)
    .map((m) => m.measureName);
  const measuresAboveAverage = takenMeasures
    .filter((m) => m.percentile != null && m.percentile > 55)
    .map((m) => m.measureName);
  const totalTakenMeasures = takenMeasures.length;

  const below = measuresBelowAverage.length;
  const above = measuresAboveAverage.length;
  const neutral = Math.max(totalTakenMeasures - (below + above), 0);
  const { label: scoreRange, n: numQualifyingMeasures } =
    (below > 1 || (totalTakenMeasures <= 1 && below === totalTakenMeasures)) ? { label: 'below HBN population average on', n: below } :
    (above > 1 || (totalTakenMeasures <= 1 && above === totalTakenMeasures)) ? { label: 'above HBN population average on', n: above } :
    (below === 1 && above === 1) ? { label: 'mixed results between', n: totalTakenMeasures } :
    (neutral > 0) ? { label: 'at about the HBN population average on', n: neutral } :
    { label: 'mixed results relative to the HBN population averagae between', n: totalTakenMeasures };

  let summary;
  const evidenceState = oddsRatio > 1 ? 'found evidence' : 'did not find evidence';
  if (diagnosisLabel === null) {
    summary = `A clinician has not yet explored this diagnosis, but InsightCare ${evidenceState} for it.`
  } else {
    summary = `A clinician labeled this diagnosis as ${diagnosisLabel}, and InsightCare ${evidenceState} for it.`
  }






  return (
    <>
      <div className="dropdownHeader">
        <span className={`dropdownArrow ${descriptionIsVisible && "open"}`} onClick={toggleDiagnosisSection}></span>
        <span className="dropdownHeader" onClick={toggleDiagnosisSection}>{diagnosisName}</span>
        <span className="whiteText content">{" ("}</span>
        <span className="whiteText emphasizedContent">{`${oddsRatio.toFixed(2)}x `}</span>
        <span className="whiteText content">{"risk in the client relative to the population)"}</span>
      </div>



      {/* <Accordion title={
        <>
          <div style={{ display: "inline" }}>{diagnosisName}</div>
          <span className="whiteText content">{" ("}</span>
          <span className="whiteText emphasizedContent">{`${oddsRatio.toFixed(2)}x `}</span>
          <span className="whiteText content">{"risk in the client relative to the population)"}</span>
        </>
      }>
        <p>{summary}</p>
      </Accordion> */}



      <div className={descriptionIsVisible ? "detailInfo" : "detailInfoInactive"}>
        {
          takenMeasures.length ?
          <>
            <p className="text content">{summary}</p>
            <p className="text emphasizedContent">Why?</p>
            <ul className="text content bullets">
              <li key="numCorrelated">
                There are
                <span className="emphasizedContent">{` ${takenMeasures.length} `}</span>
                tests with a correlation of at least 0.5 for this condition.
              </li>
              <li key="whichCorrelated">
                The client took
                <span className="emphasizedContent">{` ${totalTakenMeasures} `}</span>
                of these, and scored
                <span className="emphasizedContent">{` ${scoreRange} ${numQualifyingMeasures} `}</span> 
                out of
                <span className="emphasizedContent">{` ${totalTakenMeasures} `}</span>
                of these.
              </li>
            </ul>
            <p className="text content">The following tests were relevant to this diagnosis:</p>
            <MeasuresTable
              {...measureTableProps}
              selectionEnabled={true}
              correlationVisible={true}
              onGridReady={(params: any) => setGridApi(params.api)}
              onSelectionChanged={onSelectionChanged}
            />
            {
              selectedMeasures &&
              <MeasureGraphs
                measures={selectedMeasures}
                populationData={population}
              />
            }
          </>
          :
          <p className="text content">Loading details...</p>
        }
      </div>
    </>
  )
};

export default DiagnosisSection;